import {
  ListOption,
  Person,
  WorkItemAnnotation,
  WorkItemNote,
  WorkItemSubmission,
  WorkQueueLists,
  defaultStatusFor,
  exclusionLabel,
  formatWhen,
  statusLabel,
  teamLabel,
  todayIso
} from './workQueueLists';
import React, { useState } from 'react';

import PeoplePicker from './PeoplePicker';

/** Active options, plus the current value if it has since been retired. */
function pickable<T extends ListOption>(options: T[], current: string | null): T[] {
  return options.filter((option) => !option.retired || option.value === current);
}

interface WorkItemFormProps {
  annotation: WorkItemAnnotation | null | undefined;
  lists: WorkQueueLists;
  placeholder: string;
  /** Resolves to an error message, or null once saved. */
  onSubmit: (submission: WorkItemSubmission) => Promise<string | null>;
}

/**
 * The note form shared by both queues. Every note restates the item's full state
 * (status, owner, exclusion), so it opens pre-filled with the current values and
 * a note that only adds text changes nothing else.
 */
export const WorkItemForm: React.FC<WorkItemFormProps> = ({ annotation, lists, placeholder, onSubmit }) => {
  const [text, setText] = useState('');
  const [status, setStatus] = useState(defaultStatusFor(lists, annotation?.status));
  const [assignedTeam, setAssignedTeam] = useState(annotation?.assignedTeam ?? '');
  const [assignedTo, setAssignedTo] = useState<Person | null>(annotation?.assignedTo ?? null);
  const [exclusionType, setExclusionType] = useState(
    annotation?.excludedFromUnresolved ? annotation.exclusionType ?? '' : ''
  );
  const [excludedUntil, setExcludedUntil] = useState(
    annotation?.excludedFromUnresolved ? annotation.excludedUntil ?? '' : ''
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const statuses = lists.statuses.filter(
    (option) => !option.systemOnly && (!option.retired || option.value === status)
  );
  const exclusion = lists.exclusionTypes.find((type) => type.value === exclusionType);
  const minDate = (() => {
    const next = new Date();
    next.setDate(next.getDate() + 1);
    return next.toISOString().slice(0, 10);
  })();

  const submit = async () => {
    if (!text.trim()) {
      setError('Add some note text.');
      return;
    }
    if (exclusion?.requiresUntil && !excludedUntil) {
      setError(`${exclusion.label} exclusions need an end date.`);
      return;
    }
    if (exclusionType && excludedUntil && excludedUntil <= todayIso()) {
      setError('The exclusion end date must be in the future.');
      return;
    }
    setSaving(true);
    setError(null);
    const failure = await onSubmit({
      text: text.trim(),
      status,
      assignedTeam: assignedTeam || null,
      assignedTo,
      exclusionType: exclusionType || null,
      excludedUntil: exclusionType && excludedUntil ? excludedUntil : null
    });
    setSaving(false);
    if (failure) setError(failure);
    else setText('');
  };

  return (
    <div className="wq-form">
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        rows={2}
        placeholder={placeholder}
        aria-label="Note"
      />
      <div className="wq-form-fields">
        <label>
          <span>Status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            {statuses.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}{option.retired ? ' (retired)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Assigned team</span>
          <select value={assignedTeam} onChange={(event) => setAssignedTeam(event.target.value)}>
            <option value="">Unassigned</option>
            {pickable(lists.teams, assignedTeam || null).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}{option.retired ? ' (retired)' : ''}
              </option>
            ))}
          </select>
        </label>
        <div className="wq-form-person">
          <span>Assigned to</span>
          <PeoplePicker value={assignedTo} onChange={setAssignedTo} disabled={saving} />
        </div>
        <label>
          <span>Exclusion</span>
          <select
            value={exclusionType}
            onChange={(event) => {
              setExclusionType(event.target.value);
              if (!event.target.value) setExcludedUntil('');
            }}
          >
            <option value="">No exclusion</option>
            {pickable(lists.exclusionTypes, exclusionType || null).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}{option.retired ? ' (retired)' : ''}
              </option>
            ))}
          </select>
        </label>
        {exclusionType && (
          <label title="The item returns to open work on this date. Leave empty to exclude indefinitely.">
            <span>Excluded until{exclusion?.requiresUntil ? ' *' : ' (optional)'}</span>
            <input
              type="date"
              value={excludedUntil}
              min={minDate}
              onChange={(event) => setExcludedUntil(event.target.value)}
            />
          </label>
        )}
        <button type="button" className="btn btn-sm btn-primary" disabled={saving} onClick={submit}>
          {saving ? 'Saving…' : 'Add note'}
        </button>
      </div>
      {error && <p className="error-message wq-form-error">{error}</p>}
    </div>
  );
};

interface NoteHistoryProps {
  notes: WorkItemNote[] | undefined;
  loading: boolean;
  lists: WorkQueueLists;
  emptyText: string;
  /** Extra per-note context, e.g. what the snapshot said at the time. */
  context?: (note: WorkItemNote) => string | null;
}

/** A subject's notes, newest first, with the state each note set. */
export const NoteHistory: React.FC<NoteHistoryProps> = ({ notes, loading, lists, emptyText, context }) => {
  if (loading) return <p className="wq-history-empty">Loading notes…</p>;
  if (!notes || notes.length === 0) return <p className="wq-history-empty">{emptyText}</p>;

  return (
    <ul className="wq-history">
      {notes.map((note) => {
        const until = note.excludedUntil ?? note.reviewAfter ?? null;
        const extra = context?.(note);
        return (
          <li key={note.id} className={note.system ? 'wq-history-system' : ''}>
            <div className="wq-history-meta">
              <strong>{statusLabel(lists, note.status)}</strong>
              {note.system && <span className="wq-badge wq-badge-system">Automatic</span>}
              {' - '}{note.authorName || note.author}
              {' - '}{formatWhen(note.createdAt)}
              {note.snapshotDate && ` - snapshot ${note.snapshotDate}`}
              {extra && ` ${extra}`}
              {note.assignedTeam && ` - team ${teamLabel(lists, note.assignedTeam)}`}
              {note.assignedTo && ` - assigned to ${note.assignedTo.displayName}`}
              {note.exclusionType &&
                ` - ${exclusionLabel(lists, note.exclusionType)} exclusion${until ? ` until ${until}` : ' (no end date)'}`}
            </div>
            <div>{note.text}</div>
          </li>
        );
      })}
    </ul>
  );
};

interface WorkItemBadgesProps {
  annotation: WorkItemAnnotation | null | undefined;
  lists: WorkQueueLists;
}

/** The compact state shown in a queue row: status, closure, exclusion. */
export const WorkItemBadges: React.FC<WorkItemBadgesProps> = ({ annotation, lists }) => {
  if (!annotation) return <span className="wq-muted" title="No work-queue notes yet.">–</span>;
  const tone = annotation.addressed ? (annotation.autoResolved ? 'auto' : 'closed') : annotation.stage;
  return (
    <div className="wq-badges">
      <span
        className={`wq-status wq-status-${tone}`}
        title={`${annotation.latestNoteText || 'No note text'}\n\n${annotation.noteCount} note${annotation.noteCount === 1 ? '' : 's'}, last ${formatWhen(annotation.latestNoteAt)}`}
      >
        {statusLabel(lists, annotation.status)}
      </span>
      {annotation.addressed && annotation.closedAt && (
        <div className="wq-sub">
          closed {new Date(annotation.closedAt).toLocaleDateString()}
          {annotation.closedBy ? ` by ${annotation.closedBy}` : ''}
        </div>
      )}
      {annotation.systemProposedResolution && (
        <div
          className="wq-sub wq-warn"
          title="The snapshot shows no block, but the newest note still has the item open. The next rescan closes it as Auto-Resolved; set Resolved now to confirm it yourself."
        >
          Looks fixed - confirm
        </div>
      )}
      {annotation.excludedFromUnresolved && annotation.exclusionType && (
        <div className="wq-sub wq-excluded">
          {exclusionLabel(lists, annotation.exclusionType)}
          {annotation.excludedUntil ? ` until ${annotation.excludedUntil}` : ' (no end date)'}
        </div>
      )}
      {annotation.exclusionExpired && annotation.exclusionType && (
        <div className="wq-sub wq-warn" title="The exclusion's end date has passed, so the item is back in open work.">
          Exclusion ended {annotation.excludedUntil}
        </div>
      )}
    </div>
  );
};

interface OwnerCellProps {
  annotation: WorkItemAnnotation | null | undefined;
  lists: WorkQueueLists;
}

export const OwnerCell: React.FC<OwnerCellProps> = ({ annotation, lists }) => {
  if (!annotation?.assignedTeam && !annotation?.assignedTo) return <span className="wq-muted">Unassigned</span>;
  return (
    <div className="wq-owner">
      {annotation.assignedTo && <div title={annotation.assignedTo.upn}>{annotation.assignedTo.displayName}</div>}
      {annotation.assignedTeam && <div className="wq-sub">{teamLabel(lists, annotation.assignedTeam)}</div>}
    </div>
  );
};
